import { map } from 'lodash';
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'sharedComponents/Checkbox';
import { GeneralDialog } from 'sharedComponents/GeneralDialog';
import { SearchBox } from 'sharedComponents/SearchBox';
import { IconClose } from 'svgIcons/MotionPortalIcons';
import './style.scss';
import { reportTypes } from '../../constants';

const AssetsSelectionDialogComponent = ({
    selectedReportType,
    isShowSelectedAssetsDialog,
    selectedAssetsIdsForFleetDetailed,
    setSelectedAssetsIdsForFleetDetailed,
    setIsShowSelectedAssetsDialog,
    filteredAssets,
    onSelectedAssetCountChange
}) => {
    const { t: translate } = useTranslation();
    const [selectedAssets, setSelectedAssets] = useState(filteredAssets);
    const [searchFilter, setSearchFilter] = useState('');

    const selectedAsset = new Set(selectedAssetsIdsForFleetDetailed);
    const isAtSelectionLimit = selectedAssetsIdsForFleetDetailed.length >= 15;

    const handleSearch = useCallback((value) => {
        setSearchFilter(value);
        const lowerCaseValue = value?.toLowerCase();
        setSelectedAssets(filteredAssets.filter(({ id, name, serialNumber }) =>
            id?.toString().includes(lowerCaseValue) || name?.toLowerCase().includes(lowerCaseValue) || serialNumber?.toLowerCase().includes(lowerCaseValue)
        ));
    }, [setSearchFilter]);

    const handleToggle = useCallback((id) => {
        setSelectedAssetsIdsForFleetDetailed(prevData => {
            const isCurrentlySelected = prevData.includes(id);
            if (!isCurrentlySelected && prevData.length >= 15) {
                return prevData; // Prevent adding more than 15 assets
            }
            return isCurrentlySelected ? prevData.filter(item => item !== id) : [...prevData, id];
        });
    }, [setSelectedAssetsIdsForFleetDetailed]);

    const handleSelectedAssetsContinue = () => {
        onSelectedAssetCountChange(selectedAssetsIdsForFleetDetailed.length);
        setIsShowSelectedAssetsDialog(false);
    };

    const renderAssetsSelectionTitle = () => {
        if (selectedReportType?.reportTypeId === reportTypes.MotorFleetDetailedInstantReport) {
            return `${translate('ABB.Powertrain.Frontend.reportsGenerateSelectAssetsLabel')} | ${translate('ABB.Powertrain.Frontend.reportsTypeLabelFleetDetailedInstantReportMotors')}`;
        } else if (selectedReportType?.reportTypeId === reportTypes.DriveFleetDetailedInstantReport) {
            return `${translate('ABB.Powertrain.Frontend.reportsGenerateSelectAssetsLabel')} | ${translate('ABB.Powertrain.Frontend.reportsTypeLabelDriveFleetDetailedInstantReport')}`;
        }
    };

    return (
        <GeneralDialog
            show={isShowSelectedAssetsDialog}
            close={() => setIsShowSelectedAssetsDialog(false)}
            title={renderAssetsSelectionTitle()}
            closeButton={true}
            modalClassName='report__generator__assets-selection-dialog'
            cancelButtonProps={{
                text: translate('ABB.Powertrain.Frontend.cancelLabel'),
                onClick: () => setIsShowSelectedAssetsDialog(false)
            }}
            acceptButtonProps={{
                text: translate('ABB.Powertrain.Frontend.reportsSelectedAssetContinueLabel'),
                onClick: () => handleSelectedAssetsContinue(),
                disabled: selectedAsset.size === 0
            }}
        >
            <div className='report__generator__select-assets'>
                <div className='report__generator__select-assets__container'>
                    <h3 className='report__generator__select-assets__head'>{translate('ABB.Powertrain.Frontend.reportsGenerateSelectAssetsLabel')}</h3>
                    {filteredAssets?.length > 0 ?
                        <>
                            <div className='report__generator__select-assets__search'>
                                <SearchBox
                                    placeholder={translate('ABB.Powertrain.Frontend.reportsGenerateSearchAssetLabel')}
                                    onChange={handleSearch}
                                    value={searchFilter}
                                />
                            </div>
                            {selectedAssets?.length > 0 ?
                                <div className='report__generator__select-assets__selection'>
                                    <div className='report__generator__select-assets__all-list'>
                                        {map(selectedAssets, asset => {
                                            const isSelected = selectedAsset.has(asset?.id);
                                            const isDisabled = !isSelected && isAtSelectionLimit;
                                            return (
                                                <button
                                                    key={asset.id}
                                                    id={`report_asset_list_item_${asset.id}`}
                                                    type='button'
                                                    className='report__generator__select-assets__list'
                                                    onClick={() => handleToggle(asset?.id)}
                                                    disabled={isDisabled}
                                                    aria-pressed={isSelected}
                                                >
                                                    <Checkbox
                                                        checked={selectedAsset.has(asset?.id)}
                                                        onToggle={() => handleToggle(asset?.id)}
                                                        width={'22px'}
                                                        height={'22px'}
                                                        isStopPropagation={true}
                                                        disabled={isDisabled}
                                                    />
                                                    <span
                                                        className={`${isDisabled ? 'report__generator__select-assets__list-name--disabled' : ''} report__generator__select-assets__list-name`}
                                                        title={asset.name}
                                                    >{asset.name}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                :
                                <div className='report__generator__select-assets__not-found'>{translate('ABB.Powertrain.Frontend.reportsSelectAssetsNoAssetsFound')}</div>
                            }
                        </>
                        :
                        <div className='report__generator__select-assets__not-found'>{translate('ABB.Powertrain.Frontend.reportsSelectAssetsNoAssetsFound')}</div>
                    }
                </div>
                <div className='report__generator__select-assets__selected-container'>
                    <h3 className='report__generator__select-assets__selected-head'>
                        <span>{translate('ABB.Powertrain.Frontend.reportsGenerateSelectedAssetsLabel')}</span>
                        <span>{`${selectedAssetsIdsForFleetDetailed?.length}/15`}</span>
                    </h3>
                    <div className='report__generator__select-assets__selected-list-body'>
                        {selectedAssetsIdsForFleetDetailed?.length > 0 ?
                            filteredAssets.filter(asset => selectedAsset.has(asset?.id)).map(asset =>
                                <div key={asset?.id} className='report__generator__select-assets__selected-list'>
                                    <div className='report__generator__select-assets__selected-name'>{asset?.name}</div>
                                    <button onClick={() => handleToggle(asset?.id)}>
                                        <IconClose />
                                    </button>
                                </div>
                            )
                            :
                            <div className='report__generator__select-assets__not-selected'>{translate('ABB.Powertrain.Frontend.reportsSelectAssetsNoAssetsSelected')}</div>
                        }
                    </div>
                </div>
            </div>
        </GeneralDialog>
    );
};

export default AssetsSelectionDialogComponent;

AssetsSelectionDialogComponent.propTypes = {
    selectedReportType: PropTypes.object,
    isShowSelectedAssetsDialog: PropTypes.bool,
    selectedAssetsIdsForFleetDetailed: PropTypes.array,
    setSelectedAssetsIdsForFleetDetailed: PropTypes.func,
    setIsShowSelectedAssetsDialog: PropTypes.func,
    filteredAssets: PropTypes.array,
    onSelectedAssetCountChange: PropTypes.func
};
